import { create } from "zustand";
import type { BillNotification } from "@boo/shared";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/services/notificationApi";

interface NotificationState {
  items: BillNotification[];
  unread: number;
  load: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  items: [],
  unread: 0,
  async load() {
    try {
      const { items, unread } = await fetchNotifications();
      set({ items, unread });
    } catch {
      // the bell is best effort: keep whatever we had
    }
  },
  async markRead(id) {
    const target = get().items.find((n) => n.id === id);
    if (!target || target.readAt) return;
    set((s) => ({
      items: s.items.map((n) =>
        n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
      ),
      unread: Math.max(0, s.unread - 1),
    }));
    try {
      await markNotificationRead(id);
    } catch {
      await get().load();
    }
  },
  async markAllRead() {
    const now = new Date().toISOString();
    set((s) => ({
      items: s.items.map((n) => (n.readAt ? n : { ...n, readAt: now })),
      unread: 0,
    }));
    try {
      await markAllNotificationsRead();
    } catch {
      await get().load();
    }
  },
}));
